// Storefront design-theme registry — the SINGLE source of truth for the set of
// looks a merchant can pick for their storefront. Shared by BOTH the storefront
// (StorefrontApp resolves the active theme and stamps data-theme on .storefront-scope)
// and the Commerce-Settings theme picker (lists + previews these).
//
// A theme is a design-token set (surfaces, typography, radius, card/hero treatment)
// expressed as CSS variables under `.storefront-scope[data-theme='<id>']` in index.css.
// It NEVER overrides --shop-primary: the merchant's own brand colour, logo and hero
// always layer on top, so every theme stays personalised, not a fixed skin.
//
// `classic` is the DEFAULT and equals the original design — the --sf-* defaults on
// .storefront-scope. A shop with no/unknown storefront_theme renders as `classic`,
// so existing shops are byte-identical (fail-safe).
//
// SERVER VALIDATION (mig 344): set_storefront_settings no longer keeps a fixed id
// allowlist — it accepts any slug matching ^[a-z][a-z0-9-]{1,30}$ and normalises '' to
// 'classic'. This registry is the SINGLE source of truth for which ids actually RENDER:
// resolveTheme clamps any stored-but-unbuilt id back to `classic`. So adding a theme now
// = registry + CSS/components only, with NO migration.
//
// Some themes go beyond pure --sf-* tokens (structural bits like the boutique brush
// button + brushed header, or the modern tiles-lead / centered-hero layout). Those read
// the active id from StorefrontThemeContext and branch; `classic` never branches, so it
// stays byte-identical.

export type ThemeId =
  | 'classic'
  | 'boutique'
  | 'modern'
  | 'minimal'
  | 'magazine'
  | 'brutalist'
  | 'drop'

export const DEFAULT_THEME_ID: ThemeId = 'classic'

export interface ThemeDefinition {
  id: ThemeId
  name: string
  // One-line description shown on the picker card.
  description: string
  // Tiny keyword badges for the card (vibe cues).
  tags: string[]
}

// Ordered for display in the picker gallery (classic first — it's the default/current).
export const THEMES: Record<ThemeId, ThemeDefinition> = {
  classic: {
    id: 'classic',
    name: 'Classic',
    description: 'The original Palstro look — soft cards, light grey canvas, friendly and familiar.',
    tags: ['Default', 'Balanced'],
  },
  boutique: {
    id: 'boutique',
    name: 'Boutique',
    description: 'Editorial fashion look — hand-painted black brush buttons, centered serif headings, uppercase names and airy whitespace.',
    tags: ['Editorial', 'Serif', 'Brush'],
  },
  modern: {
    id: 'modern',
    name: 'Modern',
    description: 'Clean monochrome storefront — category tiles lead the page, a centered hero in the middle, and an airy New-Arrivals grid. Your colour accents the buttons.',
    tags: ['Minimal', 'Monochrome', 'Tiles'],
  },
  minimal: {
    id: 'minimal',
    name: 'Minimal',
    description: 'Calm and premium — off-white canvas, light type, maximum whitespace, borderless floating products and an understated underlined Add link.',
    tags: ['Calm', 'Premium', 'Airy'],
  },
  magazine: {
    id: 'magazine',
    name: 'Magazine',
    description: 'An editorial fashion spread — serif display headings, a bold masthead, an oversized feature headline over the hero, and a two-column layout with captioned products.',
    tags: ['Editorial', 'Serif', 'Big type'],
  },
  brutalist: {
    id: 'brutalist',
    name: 'Marketplace',
    description: 'A bright, Jumia-style marketplace — colour category banners, swipeable product carousels, a hamburger category drawer and a 2-up product feed. Your brand colour paints the banners and buttons.',
    tags: ['Marketplace', 'Carousels', 'Category-led'],
  },
  drop: {
    id: 'drop',
    name: 'Drop',
    description: 'Streetwear drop-culture energy — a scrolling headline, a bold hero, and one swipeable row per category behind a "Shop" banner tile. High-contrast and hype-driven.',
    tags: ['Streetwear', 'Bold', 'Rows'],
  },
}

// Display order for the gallery.
export const THEME_LIST: ThemeDefinition[] = [
  THEMES.classic,
  THEMES.boutique,
  THEMES.modern,
  THEMES.minimal,
  THEMES.magazine,
  THEMES.brutalist,
  THEMES.drop,
]

// The ids this client can RENDER (resolveTheme clamps anything else to classic). The
// server no longer mirrors this (mig 344 validates by slug pattern) — see the header note.
export const THEME_IDS: ThemeId[] = THEME_LIST.map((t) => t.id)

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && (THEME_IDS as string[]).includes(value)
}

// Resolve a stored value (possibly null / '' / unknown / cached-old) to a definition.
// Anything not in the allowlist falls back to `classic` so a junk value can never
// reach the DOM as a data-theme that has no CSS.
export function resolveTheme(value: unknown): ThemeDefinition {
  return isThemeId(value) ? THEMES[value] : THEMES[DEFAULT_THEME_ID]
}
